import React, { useState } from 'react';

import PropTypes from 'prop-types';

import AnimateHeight from '@adsk/alloy-react-animate-height';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import { useTheme } from '@adsk/alloy-react-styles';

import ActivityUser from './ActivityUser';
import { User, userShape } from '../consts';

type ActivityMultiUserChangeProps = {
  defaultIsTruncated?: boolean;
  addedUsers?: User[];
  removedUsers?: User[];
  userChangeDescription?: string;
};

/**
 * ActivityMultiUserChange component. Describes a single multi-user change activity.
 */
const ActivityMultiUserChange = React.forwardRef<
  HTMLDivElement,
  ActivityMultiUserChangeProps
>(
  (
    {
      addedUsers = [],
      removedUsers = [],
      defaultIsTruncated = true,
      userChangeDescription,
      ...props
    },
    ref,
  ) => {
    const [truncate, setTruncate] = useState(defaultIsTruncated);
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const multiUserChangeTokens =
      useTheme().tokens?.component?.activityLog?.multiUserChange;

    return (
      <div ref={ref} {...props}>
        <div
          css={[
            {
              marginBottom: truncate
                ? multiUserChangeTokens?.['description-margin-bottom-truncated']
                : multiUserChangeTokens?.['description-margin-bottom-expanded'],
            },
          ]}
        >
          {userChangeDescription ??
            T.translate('X_ADDED_X_REMOVED', {
              added: addedUsers.length,
              removed: removedUsers.length,
            })}
        </div>
        <AnimateHeight isOpen={!truncate}>
          {addedUsers?.map((user, index) => (
            <ActivityUser key={`added-user-${index}`} user={user} />
          ))}
          {removedUsers?.map((user, index) => (
            <ActivityUser key={`removed-user-${index}`} user={user} isRemoved />
          ))}
        </AnimateHeight>
        <LinkButton
          style={[
            {
              '&:focus': { boxShadow: 'none' },
              justifyContent: 'left',
              marginTop: truncate
                ? 0
                : multiUserChangeTokens?.['toggle-margin-top-expanded'],
            },
          ]}
          onClick={() => setTruncate(!truncate)}
        >
          {truncate ? T.translate('SHOW') : T.translate('HIDE')}
        </LinkButton>
      </div>
    );
  },
);

ActivityMultiUserChange.displayName = 'ActivityMultiUserChange';

ActivityMultiUserChange.propTypes = {
  /** Whether or not the users are expanded by default */
  defaultIsTruncated: PropTypes.bool,
  /** The users that were added */
  addedUsers: PropTypes.arrayOf(userShape),
  /** The users that were removed */
  removedUsers: PropTypes.arrayOf(userShape),
  /** A label to describe the user changes */
  userChangeDescription: PropTypes.string,
};

export default ActivityMultiUserChange;
